import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";

// Shared category/brand/sub-category/collection option source for every screen that filters or
// tags by them (Smart Search, Vendor Catalog Search, and the catalog upload/replace/edit forms) —
// so the "re-fetch subcategories when the selected category(ies) change, with caching/pruning"
// logic exists exactly once. Categories/brands come from the same live smart-search/ecom taxonomy
// everywhere — not the local catalog table. Collections are a separate, curated local taxonomy
// with no external source, fetched once.
//
// Sub-categories are requested via ensureSubcategoriesFor(categoryNames), called by the consumer
// whenever its own selected-category state changes (mirrors the categories/brands fetch-once,
// subcategories fetch-on-demand split every caller already had). Two shapes come back:
//   - subcategoriesByCategory: { categoryName: [{categoryName, name}] } — for taggers (upload/edit
//     forms) that need to know which category a sub-category belongs to when saving.
//   - subcategories: flat [name, ...] — for single-category filter dropdowns (Smart Search,
//     Vendor Catalog Search) that just want a name list.
// subcategoryFallback controls what ensureSubcategoriesFor([]) does:
//   - "empty" (default): no category selected -> no sub-categories (matches disabled-until-picked
//     dropdowns, e.g. Smart Search, and the tagger forms).
//   - "union": no category selected -> the full cross-category union (one request, no category
//     param), so a rep can filter by sub-category alone without picking a category first
//     (Vendor Catalog Search).
const ALL_CATEGORIES_KEY = "__all__";

const useCategoryTaxonomy = ({ subcategoryFallback = "empty" } = {}) => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({}); // { categoryName: [{categoryName, name}] }
  const [collections, setCollections] = useState([]); // [{id, name, group}] — curated, fetched once
  const fetchedKeys = useRef(new Set());
  // Tracks the most recent ensureSubcategoriesFor call's selection, so a slower/older call's
  // response — e.g. category A's fetch still in flight when the user switches to B before it
  // resolves — doesn't resurrect a category the caller has since moved away from.
  const latestWantedRef = useRef(new Set());

  const authedGet = useCallback(
    async (url) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    [instance, account]
  );

  useEffect(() => {
    if (!account) return;
    authedGet(`${urlconfig.backend_api_base_url}/api/v2/smart-search/filters`)
      .then((data) => {
        setBrands((data?.brands || []).map((b) => ({ id: b, name: b })));
        setCategories((data?.categories || []).map((c) => ({ id: c, name: c })));
      })
      .catch(() => {
        setBrands([]);
        setCategories([]);
      });
    // Collections (Occasion/Celebration tags) are a curated local taxonomy, not part of the
    // ecom smart-search filters — fetched once from their own endpoint.
    authedGet(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/collections`)
      .then((data) => setCollections(data || []))
      .catch(() => setCollections([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Fetches subcategories for any requested category not already cached, and prunes cached
  // entries no longer requested. Safe to call on every render of the caller's effect — already-
  // fetched categories are skipped. An empty categoryNames array is treated per subcategoryFallback.
  const ensureSubcategoriesFor = useCallback(
    async (categoryNames) => {
      if (!account) return;
      const wanted = new Set(
        categoryNames.length > 0
          ? categoryNames
          : subcategoryFallback === "union"
          ? [ALL_CATEGORIES_KEY]
          : []
      );
      latestWantedRef.current = wanted;

      setSubcategoriesByCategory((prev) => {
        const pruned = {};
        for (const key of Object.keys(prev)) {
          if (wanted.has(key)) pruned[key] = prev[key];
        }
        return pruned;
      });
      fetchedKeys.current = new Set([...fetchedKeys.current].filter((key) => wanted.has(key)));

      const toFetch = [...wanted].filter((key) => !fetchedKeys.current.has(key));
      if (toFetch.length === 0) return;
      toFetch.forEach((key) => fetchedKeys.current.add(key));

      const results = await Promise.all(
        toFetch.map((key) => {
          const url =
            key === ALL_CATEGORIES_KEY
              ? `${urlconfig.backend_api_base_url}/api/v2/smart-search/filters`
              : `${urlconfig.backend_api_base_url}/api/v2/smart-search/filters?category=${encodeURIComponent(key)}`;
          return authedGet(url)
            .then((data) => ({ key, subs: data?.subcategories || [] }))
            .catch(() => ({ key, subs: [] }));
        })
      );

      setSubcategoriesByCategory((prev) => {
        const next = { ...prev };
        results.forEach(({ key, subs }) => {
          // Drop results for a key no longer wanted by the latest call — this response was
          // still in flight when the caller moved on to a different selection.
          if (!latestWantedRef.current.has(key)) return;
          next[key] = subs.map((s) => ({ categoryName: key === ALL_CATEGORIES_KEY ? null : key, name: s }));
        });
        return next;
      });
    },
    [account, authedGet, subcategoryFallback]
  );

  // De-duplicated: with several categories selected, two of them can share a sub-category name.
  const subcategories = useMemo(
    () => [...new Set(Object.values(subcategoriesByCategory).flatMap((subs) => subs.map((s) => s.name)))],
    [subcategoriesByCategory]
  );

  return { brands, categories, subcategoriesByCategory, subcategories, ensureSubcategoriesFor, collections };
};

export default useCategoryTaxonomy;
